import { api } from "@/shared/api";

// FSD features/reporter — 新闻舱 × 官网联动(1009 一期:发布到官网)。
// 契约锚点:gscq-api ReporterSiteController(base /api/v1/tenant/reporter);
// 发布不扣积分(生成时已扣);时间一律 Asia/Shanghai 本地时间串(2026-10-10T09:00:00)。
// id 全是字符串(雪花 id 超 JS 安全整数)。

/** 发布记录状态(V16):已排期 / 推送中 / 已上线 / 失败 / 已取消 / 已下线。 */
export type SitePublicationStatus =
  | "SCHEDULED"
  | "PUBLISHING"
  | "PUBLISHED"
  | "FAILED"
  | "CANCELED"
  | "UNPUBLISHED";

/** 可发文章的站点(本租户已发布的宿主站)。articleListSupported:null = 宿主暂时查不到。 */
export interface SiteOption {
  siteId: string;
  name: string;
  siteUrl: string | null;
  articleListSupported: boolean | null;
  /** 非空 = 对客提示(模板没有动态新闻列表 → 只能经链接访问)。 */
  hint: string | null;
}

export interface SitePublication {
  id: string;
  articleId: string;
  draftId: string;
  siteId: string;
  siteName: string | null;
  articleTitle: string | null;
  status: SitePublicationStatus;
  scheduledAt: string | null;
  nextAttemptAt: string | null;
  publishedAt: string | null;
  siteUrl: string | null;
  /** 官网上是第几版;currentVersion = 文章当前版本;outdated = 在线但不是最新版。 */
  publishedVersion: number | null;
  currentVersion: number | null;
  outdated: boolean;
  attempts: number;
  lastError: string | null;
  /** 二期:来自官网栏目排期条目;null = 手动发布。 */
  columnItemId: string | null;
  hint: string | null;
  /** 1009 D:站点首页地址(同名站靠域名区分);站点已删 null。siteUrl 是这篇文章的地址。 */
  siteHomeUrl?: string | null;
}

export interface ArticlePublications {
  articleId: string;
  currentVersion: number | null;
  items: SitePublication[];
}

export interface DraftBadge {
  draftId: string;
  items: SitePublication[];
}

export interface HostArticle {
  hostArticleId: string;
  title: string | null;
  status: string | null;
  publishedAt: string | null;
  url: string | null;
}

export interface SiteNews {
  site: SiteOption;
  items: SitePublication[];
  hostArticles: HostArticle[];
  hostError: string | null;
}

export interface PublishToSiteRequest {
  siteId: string;
  /** 空 = 立即发;`YYYY-MM-DDTHH:mm`。 */
  scheduledAt?: string | null;
  /** 已确认导出前检查里的确认项。 */
  confirm?: boolean;
}

const BASE = "/tenant/reporter";

export const reporterSiteService = {
  sites: () => api.get<SiteOption[]>(`${BASE}/site-publish/sites`),

  publish: (articleId: number | string, req: PublishToSiteRequest) =>
    api.post<ArticlePublications>(`${BASE}/articles/${articleId}/site-publications`, req),

  byArticle: (articleId: number | string) =>
    api.get<ArticlePublications>(`${BASE}/articles/${articleId}/site-publications`),

  /** 作品列表徽标(≤100 个稿件 id)。 */
  byDrafts: (draftIds: (number | string)[]) =>
    draftIds.length === 0
      ? Promise.resolve([] as DraftBadge[])
      : api.get<DraftBadge[]>(`${BASE}/site-publications?draftIds=${draftIds.join(",")}`),

  bySite: (siteId: string) => api.get<SiteNews>(`${BASE}/sites/${siteId}/site-publications`),

  reschedule: (id: string, scheduledAt: string) =>
    api.post<ArticlePublications>(`${BASE}/site-publications/${id}/reschedule`, { scheduledAt }),

  cancel: (id: string) =>
    api.post<ArticlePublications>(`${BASE}/site-publications/${id}/cancel`, null),

  /** 推一次:立即发 / 同步到最新版 / 重新上线 / 失败重试。 */
  push: (id: string, confirm = false) =>
    api.post<ArticlePublications>(`${BASE}/site-publications/${id}/push`, { confirm }),

  unpublish: (id: string) =>
    api.post<ArticlePublications>(`${BASE}/site-publications/${id}/unpublish`, null),

  remove: (id: string) => api.delete<void>(`${BASE}/site-publications/${id}`),
};
